import React, { useEffect, useState, ChangeEvent } from 'react';
import {
  Alert as AlertMsj,
  Card,
  CardBody,
  Form,
  Button,
  Row,
  Col,
} from 'reactstrap';
import { useUser } from 'context/UserContext';
import { useAlertPlus, useConfirm } from 'hooks';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import { useHistory } from 'react-router-dom';
import { postRematriculacionRequest } from '../../features/rematriculacion/rematriculacionAPI';
import FormaDePago from './Rematriculacion/FormaDePago';
import AdjuntosRematriculacion from './Rematriculacion/AdjuntosRematriculacion';
import SuccessRequest from './SuccessRequest';
import { getSolicitudRematriculacion } from '../../features/rematriculacion/rematriculacionAPI';
import { RematriculacionRequest } from 'types/Rematriculacion';

export default function SolicitudRematriculacion() {
  const { getUser } = useUser();
  const user = getUser();
  if (!user) {
    return null;
  }

  const history = useHistory();
  const [success, setSuccess] = useState(false);

  const [] = useState<File | null>(null);

  const [formValidation, setFormValidation] = useState<
    Partial<Record<keyof RematriculacionRequest, boolean>>
  >({});

  const [disableSend, setDisableSend] = useState(false);

  const [isLoading, setLoading] = useState(true);

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const [data, setData] = useState<Partial<RematriculacionRequest>>({
    id: '',
    matriculate_id: '',
    nro_documento: user.doc,
    forma_pago: '',
    tipo_tarjeta: '',
    numero_tarjeta_cbu: '',
    attachs: [],
    motivo_rechazo: '',
    payment_id: 0,
    numero_resolucion: '',
    state_id: 0,
    es_extranjero: true,
  });

  useEffect(() => {
    setLoading(true);

    const loadSolicitud = async () => {
      try {
        const response = await getSolicitudRematriculacion();
        if (response && response.hasOwnProperty('matriculate_id')) {
          setData(response);
        } else {
          setData(prev => ({
            ...prev,
            es_extranjero: response.es_extranjero,
          }));
        }
      } catch (err) {
        alert(
          'Se produjo un error al intentar recuperar información inicial. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        setDisableSend(true);
        console.error('Home', 'Error al cargar datos iniciales');
        console.info(err);
      } finally {
        setLoading(false);
      }
    };

    loadSolicitud();
  }, []);

  const fileFields = [
    'foto_carnet',
    'titulo_adjunto',
    'fotocopia_dni_frente',
    'fotocopia_dni_dorso',
    'antecedentes_penales',
  ];

  const hasAttachment = (type: string) => {
    return data.attachs?.some(a => a.attachment_type.startsWith(type));
  };

  const handleFormValidation = () => {
    if (!validateFormaDePago()) {
      return false;
    }

    let newFormValidation = {};

    let toValidate = [
      ...Array.from(document.getElementsByClassName('form-control')),
    ] as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required);

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        if (!fileFields.includes(field.name)) {
          field.classList.add('is-invalid');
          missing = true;
        } else {
          if (!hasAttachment(field.name)) {
            field.classList.add('is-invalid');
            missing = true;
          }
        }
      } else if (field.inputMode === 'numeric') {
        if (!/^\d+$/.test(field.value)) {
          newFormValidation = {
            ...newFormValidation,
            [field.name]: false,
          };
        }
      } else {
        field.classList.remove('is-invalid');
      }
    });

    if (missing) {
      setFormValidation(newFormValidation);
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de enviar la solicitud.',
      );
      return false;
    }

    return true;
  };

  // Confirmación de envío de Solicitud
  const handleSendForm = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(
      true,
      '¿Estás seguro que deseas enviar esta solicitud de matriculación?',
      'send',
    );
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'send') {
      handleSend();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleSend = () => {
    setFormValidation({});

    setLoading(true);

    // Preparamos los archivos
    const files: Record<string, File> = {};
    const inputs = Array.from(document.querySelectorAll('input[type="file"]'));
    for (const inputElement of inputs) {
      const input = inputElement as HTMLInputElement;
      if (input.hasAttribute('required') && input.value === '') {
        alert(
          'Faltan subir archivos requeridos para poder enviar la solicitud.',
        );
        setLoading(false);
        return;
      }

      if (input.files) {
        for (let i = 0; i < input.files.length; i++) {
          files[`${input.name}_${i}`] = input.files[i];
        }
      }
    }

    postRematriculacionRequest(data, files)
      .then(res => {
        if (!res || res.status !== 'ok') {
          // Error interno
          console.error('Rematriculation', 'Error al enviar la solicitud');
          console.info(res);
          alert(
            res.message ??
            'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
          );
          setSuccess(false);
          return;
        }

        setData({
          ...data,
          motivo_rechazo: '',
        });
        setSuccess(true);
      })
      .catch(err => {
        console.error(
          'Matriculation',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          err.message ??
          'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;

    const name = target.name;

    let value: string | number | Date | null;
    value = target.value;
    if (
      typeof value === 'string' &&
      [''].includes(
        name,
      )
    ) {
      value = !!parseInt(value) ? parseInt(value) : '';
    }

    // Guardamos el nuevo valor
    setData({
      ...data,
      [name]: value,
    });
  };

  const validateFormaDePago = (): boolean => {
    const requiredFields = ['forma_pago'];
    if (!validateStep(requiredFields, false)) {
      return false;
    }

    switch (data.forma_pago) {
      case 'bancaria':
        requiredFields.push('numero_tarjeta_cbu');
        break;
      case 'tarjeta':
        requiredFields.push('tipo_tarjeta');
        requiredFields.push('numero_tarjeta_cbu');
        break;
    }
    if (!validateStep(requiredFields, true)) {
      return false;
    }

    if (data.forma_pago !== 'efectivo') {
      const field = document.getElementById('numero_tarjeta_cbu');

      if (data.forma_pago === 'bancaria') {
        if (data.numero_tarjeta_cbu?.length !== 22) {
          field?.classList.add('is-invalid');
          alert(
            'El CBU debe tener 22 dígitos.',
          );
          return false;
        }
      } else if (data.forma_pago === 'tarjeta') {
        if (data.numero_tarjeta_cbu?.length !== 16) {
          field?.classList.add('is-invalid');
          alert(
            'El número de tarjeta debe tener 16 dígitos.',
          );
          return false;
        }
      }
    }

    return true;
  };

  const validateStep = (requiredFields: string[], isForm = true): boolean => {
    let missing = false;
    // Inicializamos un objeto para la validación
    let newFormValidation = {};

    if (isForm) {
      let toValidate = [
        ...Array.from(document.getElementsByClassName('form-control')),
      ] as HTMLInputElement[];

      toValidate = toValidate.filter(e => !e.disabled && e.required && requiredFields.includes(e.name));

      // Validar campos required
      toValidate.forEach(field => {
        if (field.required && !field.disabled && !field.value?.trim()) {
          if (!fileFields.includes(field.name)) {
            field.classList.add('is-invalid');
            missing = true;
          } else {
            if (!hasAttachment(field.name)) {
              field.classList.add('is-invalid');
              missing = true;
            }
          }
        } else if (field.inputMode === 'numeric') {
          if (!/^\d+$/.test(field.value)) {
            newFormValidation = {
              ...newFormValidation,
              [field.name]: false,
            };
          }
        } else {
          field.classList.remove('is-invalid');
        }
      });
    } else {
      requiredFields.map(field => {
        missing = missing || !data.hasOwnProperty(field) || !(data as any)[field];
      });
    }

    if (missing) {
      setFormValidation(newFormValidation);
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de continuar.',
      );
      return false;
    }

    return true;
  };

  return (
    <div className="content">
      <Col sm={4}>
        <Button
          className="btn-round"
          color="primary"
          onClick={() => history.push('/inicio')}
        >
          Volver
        </Button>
      </Col>
      {data.state_id === 3 && data.motivo_rechazo
        ?
          <AlertMsj color="danger">
            <Row className="mb-2">
              <Col sm={12}>
                <span className='group-name observations'>
                  <strong>Motivo Rechazo</strong>
                  <p className="mt-2">
                    { data.motivo_rechazo }
                  </p>
                </span>
              </Col>
            </Row>
          </AlertMsj>
        : null
      }
      <Row>
        <Col sm={12}>
          <Card>
            <Row className="justify-content-center mt-3">
              <Col sm="auto">
                <p
                  style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    fontStyle: 'medium',
                    marginBottom: -1,
                  }}
                >
                  Solicitud de Rematriculación
                </p>
              </Col>
            </Row>
            {success ? (
              <SuccessRequest />
            ) : (
              <CardBody style={{ paddingTop: 0 }}>
                <Form>
                  <Row className="justify-content-center">
                    <Col sm="auto" className="text-center px-0 mt-3">
                      <span
                        style={{
                          fontSize: '1rem',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                        }}
                      >
                        Pago
                      </span>
                    </Col>
                  </Row>
                  <FormaDePago
                    data={data}
                    formValidation={formValidation}
                    handleInputChange={handleInputChange}
                    isLoading={isLoading}
                    disableSend={disableSend}
                  />

                  <Row className="justify-content-center">
                    <Col sm="auto" className="text-center px-0 mt-3">
                      <span
                        style={{
                          fontSize: '1rem',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                        }}
                      >
                        Adjuntos
                      </span>
                    </Col>
                  </Row>
                  <AdjuntosRematriculacion
                    data={data}
                    isLoading={isLoading}
                    disableSend={disableSend}
                  />
                  <div
                    style={{ marginTop: '5rem' }}
                    className='d-flex justify-content-center'
                  >
                    <Button
                      type='button'
                      className='btn-custom'
                      disabled={isLoading || disableSend}
                      onClick={() => {
                        handleSendForm();
                      }}
                    >
                      Solicitar Rematriculación
                    </Button>
                  </div>
                </Form>
                <AlertModal
                  isOpen={showAlert}
                  message={message}
                  onClose={alertCallback}
                />
                <ConfirmModal
                  isOpen={confirm}
                  title={confirmTitle}
                  action={confirmAction}
                  onClose={() => setConfirm(false)}
                  onConfirm={handleConfirmation}
                />
              </CardBody>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
